/* eslint-disable @typescript-eslint/ban-ts-comment */
import { defineStore } from "pinia"
import { HttpService, WITHOUT_SESSION_RECOVERY } from "@/helpers/http-service"
import { CONSTANT } from "@/helpers/constant"
import { LoginUser } from "@/types/login"
import { router } from "@/router"
import _ from "lodash"
import { useCustomerStore } from "@/stores/customer.store"
import { RecoverPassword } from "@/models/recover.model"
import { RecoverCreatePassword } from "@/models/recoverCreate.model"
import { _getValue } from "@/helpers/common"
import { ApiResponse } from "@/types/apiResponse"
import { SessionRefreshOutcome } from "@/types/session"
import { useSystemAndContextStore } from "@/stores/systemAndContext.store"
import { withSessionLock } from "@/helpers/session-lock"
const httpService = new HttpService()

export const useAuthStore = defineStore({
  id: "auth",
  state: () => ({
    contextToken: localStorage.getItem("contextToken") || "",
    // The credential that survives the context token (PORTAL-6084). Single use: every
    // refresh hands back a new one, so whatever is stored here is only ever the latest.
    refreshToken: localStorage.getItem("refreshToken") || "",
    salesRepContextToken: localStorage.getItem("salesRepContextToken") || "",
    salesRepCustomer: JSON.parse(localStorage.getItem("salesRepCustomer")!),
    returnUrl: "",
    errorMessage: "",
    successMessage: "",
  }),
  getters: {
    /** A sales rep is currently looking at a customer's account. */
    isImpersonating: (state): boolean => Boolean(state.salesRepContextToken || state.salesRepCustomer),
  },
  actions: {
    async login(loginUserRequest: LoginUser) {
      try {
        const cookieConsent = localStorage.getItem("cookieConsent")
        const teamPortalLang = localStorage.getItem("teamportal-lang")
        const teamPortalLangId = localStorage.getItem("teamportal-lang-id")
        localStorage.clear()
        // localStorage.clear() took the stored one; the state copy has to go with it, or a
        // failed login leaves the previous user's refresh token in memory.
        this.clearRefreshToken()
        if (cookieConsent) {
          localStorage.setItem("cookieConsent", cookieConsent)
        }
        if (teamPortalLang) {
          localStorage.setItem("teamportal-lang", teamPortalLang)
        }
        if (teamPortalLangId) {
          localStorage.setItem("teamportal-lang-id", teamPortalLangId)
        }
        const result: ApiResponse = await httpService.post(`${CONSTANT.API.ACCOUNT_LOGIN}`, { ...loginUserRequest })
        if (result && result.status === CONSTANT.HTTP_STATUS.OK) {
          this.contextToken = _.get(result, "data.contextToken", "")
          if (this.contextToken) {
            localStorage.setItem("contextToken", this.contextToken)

            const systemAndContextStore = useSystemAndContextStore()
            await systemAndContextStore.fetchCurrentContext()

            const customerStore = await useCustomerStore()
            // Reported by return value, not by `errorMessage`: that field is shared UI state
            // this action does not own, and a message left over from an earlier failure used
            // to make a good login look failed (RISK-04).
            const customerLoaded = await customerStore.fetchCustomerInfo()
            if (!customerLoaded) {
              // No refresh token for a session we could not confirm. Minting one would hand
              // out a 30-day credential for a login that never finished, and the user is
              // about to be sent back to the form anyway (RISK-03).
              this.errorMessage = `eecom-teamportal.error.customerIsNotLoggedInError`
              return
            }

            // After the session exists, never before: minting requires a live one.
            await this.issueRefreshToken()
          } else {
            this.errorMessage = `eecom-teamportal.error.loginError`
          }
        } else {
          this.errorMessage = `eecom-teamportal.error.loginError`
        }
      } catch (error) {
        const errors = _.get(error, `response.data.errors`, [])
        if (errors && errors.length > 0) {
          const currentError: any = errors[0]
          if (currentError.status === CONSTANT.HTTP_STATUS.UNAUTHORIZED.toString()) {
            this.errorMessage = `eecom-teamportal.error.loginError`
          } else {
            this.errorMessage = `eecom-teamportal.error.somethingWentWrongError`
          }
        }

        console.error(error)
      }
    },
    async logout() {
      // Stepping out of an impersonation is not a logout. The rep hands the customer's account back
      // and returns to their own, and `login-with-customerId` performs that whole switch on its own:
      // it reads `impersonatedBy` off the session row the rep is sitting in to learn who is
      // returning, closes the opened account, clears the impersonation markers, and
      // `fetchCustomerInfo()` lands the rep back on the sales-force dashboard.
      //
      // So no `account/logout` is posted on this path. Posting it first was only ever harmless
      // because core left the logged-out session row behind: the return call sends no
      // `salesRepContextToken`, and the `salesRepId` it does send is ignored server-side on purpose,
      // so that row is the only answer to "which rep is this?". Now that logging out really ends the
      // session, the POST would take the answer with it and strand the rep on the login page
      // (PORTAL-6084).
      if (this.isImpersonating) {
        const salesRepId = _getValue(this.salesRepCustomer, "id", "").value

        if (salesRepId && (await this.loginWithCustomerId(salesRepId, CONSTANT.LOGIN_TYPE.SALES_REP_CUSTOMER, false))) {
          return
        }

        // The way back failed, so the rep is still sitting in the customer's account. Drop the
        // markers and fall through to a real logout rather than leave them stranded in it.
        this.endImpersonation()
      }

      try {
        const result: ApiResponse = await httpService.post(`${CONSTANT.API.ACCOUNT_LOGOUT}`)
        if (result && result.status === CONSTANT.HTTP_STATUS.OK) {
          const cookieConsent = localStorage.getItem("cookieConsent")
          const teamPortalLang = localStorage.getItem("teamportal-lang")
          const teamPortalLangId = localStorage.getItem("teamportal-lang-id")
          localStorage.removeItem("contextToken")
          localStorage.removeItem("customer")
          localStorage.clear()
          this.contextToken = ""
          // The logout revoked it server-side; a stale copy would only buy a 401.
          this.clearRefreshToken()
          const customerStore = await useCustomerStore()
          customerStore.customer = null
          if (cookieConsent) {
            localStorage.setItem("cookieConsent", cookieConsent)
          }
          if (teamPortalLang) {
            localStorage.setItem("teamportal-lang", teamPortalLang)
          }
          if (teamPortalLangId) {
            localStorage.setItem("teamportal-lang-id", teamPortalLangId)
          }
          await router.push(CONSTANT.ROUTE.PATH.LOGIN)
        }
      } catch (error) {
        console.error(error)
      }
    },
    async recoverPassword(recoverPassword: RecoverCreatePassword) {
      try {
        this.contextToken = ""
        const result: ApiResponse = await httpService.post(`${CONSTANT.API.ACCOUNT_RECOVER_PASSWORD}`, { ...recoverPassword })
        if (result && result.status === CONSTANT.HTTP_STATUS.OK) {
          if (_.get(result, "data.success", "")) {
            this.successMessage = `eecom-teamportal.success.recoverPasswordSuccess`
          } else {
            this.errorMessage = `eecom-teamportal.error.passwordRecoveryRequestError`
          }
        } else {
          this.errorMessage = `eecom-teamportal.error.passwordRecoveryRequestError`
        }
      } catch (error) {
        this.contextToken = ""
        this.errorMessage = `eecom-teamportal.error.passwordRecoveryRequestError`
        console.error(error)
      }
      this.contextToken = ""
    },
    async recoverPasswordConfirm(recoverPassword: RecoverPassword) {
      try {
        this.contextToken = ""
        const result: ApiResponse = await httpService.post(`${CONSTANT.API.ACCOUNT_RECOVER_PASSWORD_CONFIRM}`, { ...recoverPassword })
        if (result && result.status === CONSTANT.HTTP_STATUS.OK) {
          if (_.get(result, "data.success", "")) {
            return
          }
          this.errorMessage = `eecom-teamportal.error.passwordRecoveryConfirmError`
        } else {
          this.errorMessage = `eecom-teamportal.error.passwordRecoveryConfirmError`
        }
      } catch (error) {
        this.contextToken = ""
        this.errorMessage = `eecom-teamportal.error.passwordRecoveryConfirmError`
        const errors = _.get(error, "response.data.errors", [])
        for (let i = 0; i < errors.length; i++) {
          // @ts-ignore
          if (errors[i].code === "CHECKOUT__CUSTOMER_RECOVERY_HASH_EXPIRED") {
            this.errorMessage = `eecom-teamportal.error.passwordRecoveryConfirmErrorExpired`
            return
          }
        }
        console.error(error)
      }
      this.contextToken = ""
    },
    async setContextToken(token: string) {
      localStorage.setItem("", token)
      this.contextToken = token
    },
    /**
     * Asks the backend for a refresh token for the session that is currently open.
     *
     * Best effort on purpose: a login that works but cannot mint one is still a login, it
     * just will not survive the context expiring. The backend refuses to mint for a session
     * a sales rep opened on a customer's behalf (that would leave the rep holding a 30-day
     * key to someone else's account), so a 403 here is an expected answer and not a failure
     * to report — the rep keeps the token minted for their own session.
     *
     * Sent `WITHOUT_SESSION_RECOVERY`: a mint on a dead context has nothing to gain from a
     * recovery, and the one at app start holds the session lock that a recovery would wait for.
     */
    async issueRefreshToken(): Promise<boolean> {
      try {
        const result: ApiResponse = await httpService.post(`${CONSTANT.API.SESSION_TOKEN}`, undefined, WITHOUT_SESSION_RECOVERY)
        const refreshToken = _.get(result, "data.refreshToken", "")
        if (!refreshToken) {
          return false
        }
        this.setRefreshToken(refreshToken)
        return true
      } catch (error) {
        console.debug("No refresh token issued for this session.", error)
        return false
      }
    },
    /**
     * App start: gives a session that has a context but no refresh token one, unless another tab
     * has stored one in the meantime.
     *
     * Tabs the browser restores together all start like that, and each used to mint its own. The
     * last write won storage, and the other tabs were left remembering a token that was not the
     * stored one (PORTAL-6084 report v3, RISK-02). Under the session lock the first tab mints and
     * the others find its token.
     */
    async ensureRefreshToken(): Promise<void> {
      await withSessionLock(async () => {
        const storedToken = localStorage.getItem("refreshToken") || ""
        if (storedToken) {
          this.refreshToken = storedToken
          return
        }
        await this.issueRefreshToken()
      })
    },
    /**
     * Rebuilds the session from the stored refresh token and reports what came back, so the
     * HTTP interceptor can decide between a silent retry, a rejection and a logout.
     *
     * This replaces re-logging in with the stored `customerId`: that id was never a
     * credential, and posting it to login-with-customerId restored a teamportal sub-user as
     * the club owner — with the owner's full rights (PORTAL-6084).
     *
     * The token is single use and the backend spends it on the way in, so it leaves storage
     * before the POST: a copy left behind after a refusal would only produce the same failure
     * again. A request the backend never answered is the exception. Offline, timed out, rate
     * limited or a 5xx from a proxy during a deploy says nothing about the token, so it goes back
     * and the outcome is `unavailable` rather than a logout (PORTAL-6084 report v3, RISK-03). If
     * the backend did spend it and only the answer was lost, the next attempt gets a 401 and ends
     * where a refusal ends anyway, just later.
     *
     * Two cases make this more than a single POST:
     *
     *  - A rep sitting in a customer's account holds a refresh token for their *own* session;
     *    the impersonated one was never allowed to mint any. Spending it ends the
     *    impersonation rather than renewing it, and the rep lands back in their own account
     *    instead of on the login screen — which is friendly, but means the restored session
     *    belongs to somebody else than the request that triggered the recovery. Hence
     *    `identity-changed`: a success nobody may replay a request into (RISK-01).
     *  - Tabs share the session through localStorage, so several of them notice a dead context
     *    and reach for the same single-use token. The work happens under a cross-tab lock, and
     *    the holder decides from storage, not from what this tab remembers (report v3, RISK-02).
     *    A stored context token other than the dead one means a sibling restored or signed in a
     *    session. That session is taken over, whether or not a rotated token came with it. The
     *    same dead context means nobody did, and whatever token storage holds now is spent, even
     *    if it is not the one this tab remembers: tabs restored together each mint one, and the
     *    last write wins. A taken-over session can turn out to be dead as well, for example when
     *    two tabs were left open overnight. In that case the token stored with it is spent.
     */
    async refreshSession(): Promise<SessionRefreshOutcome> {
      // Sampled before the lock is requested: while this tab waits for it, a sibling can
      // restore the session and replace the context token in storage, and the comparison
      // below is what recognises that.
      const deadContextToken = this.contextToken
      const wasImpersonating = this.isImpersonating

      return withSessionLock(async (): Promise<SessionRefreshOutcome> => {
        const storedContextToken = localStorage.getItem("contextToken") || ""

        if (storedContextToken && storedContextToken !== deadContextToken) {
          this.contextToken = storedContextToken
          this.refreshToken = localStorage.getItem("refreshToken") || ""
          const adopted = await this.completeSessionRestore(wasImpersonating)
          if (adopted !== "failed") {
            return adopted
          }
        }

        const storedToken = localStorage.getItem("refreshToken") || ""
        if (!storedToken) {
          // No tab ever held one, or a sibling's refresh failed and cleared the key.
          this.clearRefreshToken()
          return "failed"
        }

        // Compare-and-clear rather than a plain remove: where the lock is unavailable a
        // sibling may have rotated the token between the read above and this line, and its
        // replacement must not be deleted by a tab that never owned it.
        this.clearRefreshToken(storedToken)

        try {
          const result: ApiResponse = await httpService.post(`${CONSTANT.API.SESSION_REFRESH}`, { refreshToken: storedToken })
          const token = _.get(result, "data.token", "")
          const rotatedToken = _.get(result, "data.refreshToken", "")

          if (!token) {
            return "failed"
          }

          this.contextToken = token
          localStorage.setItem("contextToken", this.contextToken)
          if (rotatedToken) {
            this.setRefreshToken(rotatedToken)
          }

          return await this.completeSessionRestore(wasImpersonating)
        } catch (error) {
          console.error(error)
          // A status below 500 is the backend's verdict on the token. No status at all, a 429 or a
          // 5xx is no verdict, and the token goes back unless a sibling stored a newer one meanwhile.
          const status = Number(_.get(error, "response.status", 0))
          if (!status || status === 429 || status >= 500) {
            const tokenNow = localStorage.getItem("refreshToken")
            if (tokenNow) {
              this.refreshToken = tokenNow
            } else {
              this.setRefreshToken(storedToken)
            }
            return "unavailable"
          }
          return "failed"
        }
      })
    },
    /**
     * Shared tail of a refresh: adopt the restored session into the stores, read it back, and
     * say whether it is still the same account.
     *
     * Internal to `refreshSession`. `wasImpersonating` is passed in because it has to be
     * sampled before the session is replaced — by the time this runs, the state that says a
     * customer is open is about to be cleared.
     *
     * Two ways for a restored session to belong to someone else, both of which must stop a
     * request from being replayed into it (RISK-01): the rep whose view of a customer
     * expired and is handed back their own account, and a sibling tab that signed a
     * different contact in while this tab sat on a session that had already died. The second
     * is why the account is compared and not only the impersonation flag — a tab that adopts
     * a sibling's token adopts whoever that sibling is signed in as.
     *
     * The contact is read back without navigating. A recovery should go unnoticed: the request
     * is replayed into the view it came from, and a redirect to the dashboard would throw away
     * that view and whatever was typed into it (report v3, RISK-01). Only `identity-changed`
     * needs a new page, and the interceptor sends the user there after the session lock is
     * released.
     */
    async completeSessionRestore(wasImpersonating: boolean): Promise<SessionRefreshOutcome> {
      this.endImpersonation()

      const systemAndContextStore = useSystemAndContextStore()
      systemAndContextStore.currentContext = null

      const customerStore = useCustomerStore()
      const previousCustomerId = _.get(customerStore, "customer.id", "")
      const customerLoaded = await customerStore.fetchCustomerInfo(true)
      await systemAndContextStore.fetchCurrentContext()

      if (!customerLoaded) {
        return "failed"
      }

      // Only a *known* mismatch counts. Either id can legitimately be unknown — a session
      // that expired before the contact was ever read, for one — and treating "cannot tell"
      // as a change would refuse the silent retry in exactly the case it was built for.
      const restoredCustomerId = _.get(customerStore, "customer.id", "")
      const accountChanged = Boolean(previousCustomerId) && Boolean(restoredCustomerId) && previousCustomerId !== restoredCustomerId

      return wasImpersonating || accountChanged ? "identity-changed" : "recovered"
    },
    setRefreshToken(token: string) {
      this.refreshToken = token
      localStorage.setItem("refreshToken", token)
    },
    /**
     * Drops the refresh token. Given `expected`, only a stored copy that still *is* that
     * token is removed: a sibling tab may have rotated it in the meantime, and deleting the
     * replacement would end a session that is alive and log every tab out (RISK-02). The
     * in-memory copy goes either way — whatever this tab held is spent — and is replaced by
     * the newer one when storage has moved on, so state and storage stay in step.
     */
    clearRefreshToken(expected?: string) {
      const storedToken = localStorage.getItem("refreshToken")
      if (expected !== undefined && storedToken !== null && storedToken !== expected) {
        this.refreshToken = storedToken
        return
      }
      this.refreshToken = ""
      localStorage.removeItem("refreshToken")
    },
    /**
     * Drops the "a rep is looking at a customer" state. The rep's own token was consumed when
     * the customer's account was opened, so there is nothing to restore it to.
     */
    endImpersonation() {
      if (!this.salesRepContextToken && !this.salesRepCustomer) {
        return
      }
      this.salesRepContextToken = ""
      this.salesRepCustomer = null
      localStorage.removeItem("salesRepContextToken")
      localStorage.removeItem("salesRepCustomer")
    },
    /**
     * Returns whether the contact is logged in afterwards. Callers used to infer that
     * from `errorMessage`, which is shared UI state other auth actions write to — a
     * left-over message from an earlier failure then made every later login look
     * failed. The field is cleared here as well, so it only ever describes this call.
     */
    async loginWithCustomerId(contactId: any, loginType?: string, isExternalRedirect?: boolean): Promise<boolean> {
      const apiUrl = CONSTANT.API.SALES_FORCE_CUSTOMER_ACCOUNT_LOGIN_WITH_CUSTOMER_ID
      this.errorMessage = ""
      try {
        const customerStore = await useCustomerStore()
        let requestData = {}
        if (loginType !== CONSTANT.LOGIN_TYPE.SALES_REP_CUSTOMER) {
          requestData = {
            loginType: loginType ?? null,
            customerId: contactId,
            salesRepId: customerStore.customer ? customerStore.customer.id : "",
            salesRepContextToken: customerStore.customer && loginType !== CONSTANT.LOGIN_TYPE.SALES_REP_CUSTOMER ? this.contextToken : "",
          }
        } else {
          requestData = {
            loginType: loginType ?? null,
            salesRepId: contactId,
          }
        }
        const systemAndContextStore = useSystemAndContextStore()
        const result: any = await httpService.post(`${apiUrl}`, requestData)
        if (result && result.status === CONSTANT.HTTP_STATUS.OK) {
          const customerStore = useCustomerStore()
          const salesRep = customerStore.isSalesRep;

          if (salesRep && loginType !== CONSTANT.LOGIN_TYPE.SALES_REP_CUSTOMER && !this.salesRepCustomer && !this.salesRepContextToken) {
            if (customerStore.customer) {
              this.salesRepCustomer = customerStore.customer
              localStorage.setItem("salesRepCustomer", JSON.stringify(this.salesRepCustomer))
            }
            if (this.contextToken) {
              this.salesRepContextToken = this.contextToken
              localStorage.setItem("salesRepContextToken", this.salesRepContextToken)
              localStorage.removeItem("contextToken")
              localStorage.removeItem("customer")
              this.contextToken = ""
              customerStore.customer = null
            }
          }
          systemAndContextStore.currentContext = null
          const { token } = result.data
          if (token) {
            this.contextToken = token
            localStorage.setItem("contextToken", this.contextToken)
          }
          if (this.contextToken) {
            if (loginType === CONSTANT.LOGIN_TYPE.SALES_REP_CUSTOMER) {
              if (this.salesRepContextToken) {
                this.salesRepContextToken = ""
                this.salesRepCustomer = null
                localStorage.removeItem("salesRepContextToken")
                localStorage.removeItem("salesRepCustomer")
              }
            }
            const customerLoaded = await customerStore.fetchCustomerInfo(isExternalRedirect)
            await systemAndContextStore.fetchCurrentContext()
            this.errorMessage = customerLoaded ? "" : `eecom-teamportal.error.customerIsNotLoggedInError`

            return customerLoaded
          } else {
            this.errorMessage = `eecom-teamportal.error.loginError`
          }
        } else {
          this.errorMessage = `eecom-teamportal.error.customerIsNotLoggedInError`
        }
      } catch (error) {
        const errors: any = _.get(error, `response.data.errors`, [])
        if (errors && errors.length > 0) {
          this.errorMessage = errors[0].detail
        }
        console.error(error)
      }

      return false
    },
  },
})
